/**
 * GncpDraftSuggestionCard — Renderer für 'gncp_draft'-Suggestions
 * aus cap-suggest-gncp.
 *
 * Zeigt:
 *   - "GNCP-Entwurf bereit"-Badge mit Sparkles-Icon
 *   - Zähler (Einordnung: N · Maßnahmen: N · Verlauf: N)
 *   - Kurze Rationale (KEINE Notiz-Klartext-Wiedergabe — der lebt
 *     im Beratungs-Tab)
 *   - Primary Action "Im GNCP-Tab öffnen" → /patients/{id}?tab=beratung
 *     (gncp ist sub-tab innerhalb beratung; ?tab=gncp würde von
 *     PATIENT_TAB_REDIRECTS sowieso auf beratung umgeleitet)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Sparkles, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isGncpDraftPayload,
  type AiSuggestion,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

export const GncpDraftSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();

  if (!isGncpDraftPayload(suggestion.payload)) return null;
  const p = suggestion.payload;

  const totalFields = p.diagnosis_count + p.intervention_count + p.monitoring_count;

  const handlePrimary = async () => {
    if (suggestion.patient_id) {
      navigate(`/patients/${suggestion.patient_id}?tab=beratung`);
    }
    await resolve(suggestion, 'accepted', 'open_gncp');
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-900 dark:bg-violet-950 dark:text-violet-100">
            <Sparkles className="h-3 w-3" />
            GNCP-Entwurf bereit
          </span>
          <span className="text-xs text-muted-foreground">
            {totalFields > 0
              ? `${totalFields} Felder extrahiert`
              : 'Entwurf bereit zur Prüfung'}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="mb-2 flex flex-wrap gap-1.5">
        <Badge variant="secondary" className="text-[10px] font-normal">
          Einordnung: {p.diagnosis_count}
        </Badge>
        <Badge variant="secondary" className="text-[10px] font-normal">
          Maßnahmen: {p.intervention_count}
        </Badge>
        <Badge variant="secondary" className="text-[10px] font-normal">
          Monitoring: {p.monitoring_count}
        </Badge>
      </div>

      <p className="mb-3 text-sm text-foreground">{p.rationale}</p>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={handlePrimary}>
          Im GNCP-Tab öffnen
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
        {p.suggested_actions.slice(0, 2).map((a, i) => {
          // Skip the open_gncp action — it's the primary button already.
          if (a.kind === 'open_gncp') return null;
          return (
            <Badge
              key={`${a.kind}-${i}`}
              variant="outline"
              className="text-xs font-normal"
            >
              {a.label}
            </Badge>
          );
        })}
      </div>
    </article>
  );
};
